import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Eraser, RotateCcw } from 'lucide-react';

import { readSelection } from '../../lib/offsets.js';
import { findOverlap } from '../../lib/spans.js';
import { humanAddedSpans } from '../../lib/remap.js';
import { useEditorStore } from '../../store/editorStore.js';
import { useUiStore } from '../../store/uiStore.js';
import { Keycap } from '../common/index.jsx';
import { CaptionSurface } from './CaptionSurface.jsx';
import { FinalPreview } from './FinalPreview.jsx';
import { SelectionPopover } from './SelectionPopover.jsx';
import { SpanPopover } from './SpanPopover.jsx';
import { SpanRail } from './SpanRail.jsx';

/**
 * The caption currently being annotated.
 *
 * Only one is mounted at a time; CaptionSwitcher handles moving between the three.
 */
export function CaptionCard({ draft, slot, extraRanges, readOnly = false, onClear, children }) {
  const surfaceRef = useRef(null);

  const selection = useEditorStore((s) => s.selection);
  const setSelection = useEditorStore((s) => s.setSelection);
  const spanCursor = useEditorStore((s) => s.spanCursor);
  const cursorScroll = useEditorStore((s) => s.cursorScroll);
  const setCursor = useEditorStore((s) => s.setCursor);
  const addTag = useEditorStore((s) => s.addTag);
  const updateSpan = useEditorStore((s) => s.updateSpan);
  const retagSpan = useEditorStore((s) => s.retagSpan);
  const removeSpan = useEditorStore((s) => s.removeSpan);
  const restoreSpan = useEditorStore((s) => s.restoreSpan);
  const commitCaptionEdit = useEditorStore((s) => s.commitCaptionEdit);
  const undoCaptionEdit = useEditorStore((s) => s.undoCaptionEdit);
  const dismissRemapReport = useEditorStore((s) => s.dismissRemapReport);
  const setFinalCaption = useEditorStore((s) => s.setFinalCaption);
  const spanPopover = useEditorStore((s) => s.spanPopover);
  const openSpanPopover = useEditorStore((s) => s.openSpanPopover);
  const closeSpanPopover = useEditorStore((s) => s.closeSpanPopover);

  const showTagLetters = useUiStore((s) => s.showTagLetters);
  const toast = useUiStore((s) => s.toast);

  const captureSelection = useCallback(
    (event) => {
      if (readOnly) return;
      setSelection(readSelection(surfaceRef.current));
    },
    [readOnly, setSelection],
  );

  // Bring the span into view only when the cursor was MOVED (j/k, or a tag just
  // created). Clicking a mark or hovering the rail must not scroll the page.
  useEffect(() => {
    if (spanCursor < 0 || !cursorScroll) return;
    const node = surfaceRef.current?.querySelector(`mark[data-span-id="${spanCursor}"]`);
    if (node) {
      node.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
      node.focus?.({ preventScroll: true });
    }
  }, [spanCursor, cursorScroll]);

  // Show what the annotator has typed into the caption, so additions are as visible
  // here as the tags are - previously they only appeared in the final preview.
  const addedRanges = useMemo(
    () => humanAddedSpans(draft.agentCaption, draft.working, draft.spans),
    [draft.agentCaption, draft.working, draft.spans],
  );

  const overlap = selection ? findOverlap(draft.spans, selection) : null;

  return (
    <section className="caption-card focused" aria-label={`Caption ${draft.label}`}>
      <div className="caption-card-body">
        {draft.remapReport && (
          <div className="remap-report" role="alert">
            <span className="grow">
              {draft.remapReport.moved > 0 &&
                `${draft.remapReport.moved} tag${draft.remapReport.moved === 1 ? '' : 's'} moved`}
              {draft.remapReport.moved > 0 && draft.remapReport.dropped.length > 0 && ' · '}
              {draft.remapReport.dropped.length > 0 &&
                `${draft.remapReport.dropped.length} tag${
                  draft.remapReport.dropped.length === 1 ? '' : 's'
                } could not be re-anchored and were dropped`}
            </span>
            <button type="button" className="btn sm" onClick={() => undoCaptionEdit(slot)}>
              <RotateCcw size={13} /> Undo edit
            </button>
            <button type="button" className="btn sm ghost" onClick={() => dismissRemapReport(slot)}>
              Dismiss
            </button>
          </div>
        )}

        <div className="row-between">
          <span className="section-title" style={{ margin: 0 }}>
            Caption {draft.label} — type to edit it, or select text to tag it
          </span>
          {!readOnly && (
            <div className="row gap-1">
              <button
                type="button"
                className="btn sm ghost"
                title="Remove every tag and undo your caption edits, back to the original"
                onClick={() => onClear?.(slot)}
              >
                <Eraser size={13} /> Clear
              </button>
            </div>
          )}
        </div>

        <CaptionSurface
          ref={surfaceRef}
          text={draft.working}
          spans={draft.spans}
          addedSpans={addedRanges}
          extraRanges={extraRanges}
          activeSpanIndex={spanCursor}
          showTagLetters={showTagLetters}
          editable
          onCommit={(next) => {
            const report = commitCaptionEdit(slot, next);
            if (report?.dropped) {
              toast(`${report.dropped} tag(s) could not be re-anchored`, { tone: 'danger' });
            }
          }}
          onSpanClick={(index, event) =>
            openSpanPopover(index, event.currentTarget.getBoundingClientRect())
          }
          onMouseUp={captureSelection}
          onKeyUp={(e) => e.shiftKey && captureSelection(e)}
        />

        {!readOnly && (
          <SpanRail
            spans={draft.spans}
            cursor={spanCursor}
            onCursor={setCursor}
            onUpdate={updateSpan}
            onRetag={retagSpan}
            onRemove={(index) => {
              const removed = removeSpan(index);
              if (removed) {
                toast(`Removed the tag on "${truncate(removed.text)}"`, {
                  action: { label: 'Undo', onClick: () => restoreSpan(removed) },
                });
              }
            }}
          />
        )}

        <FinalPreview
          draft={draft}
          onChange={(text) => setFinalCaption(slot, text)}
          onResync={(text) => setFinalCaption(slot, text)}
        />

        {children}
      </div>

      {!readOnly && spanPopover && draft.spans[spanPopover.index] && (
        <SpanPopover
          span={draft.spans[spanPopover.index]}
          index={spanPopover.index}
          anchor={spanPopover.anchor}
          onUpdate={updateSpan}
          onRetag={retagSpan}
          onRemove={(index) => {
            const removed = removeSpan(index);
            if (removed) {
              toast(`Removed the tag on "${truncate(removed.text)}"`, {
                action: { label: 'Undo', onClick: () => restoreSpan(removed) },
              });
            }
          }}
          onClose={closeSpanPopover}
        />
      )}

      {!readOnly && !spanPopover && (
        <SelectionPopover
          selection={selection}
          overlap={overlap}
          onTag={(label) => {
            if (addTag(label) === null) {
              toast('That selection overlaps an existing tag', { tone: 'danger' });
            }
          }}
          onGoToOverlap={() => {
            setCursor(draft.spans.indexOf(overlap));
            setSelection(null);
          }}
          onDismiss={() => setSelection(null)}
        />
      )}
    </section>
  );
}

function truncate(text, max = 32) {
  const clean = String(text || '');
  return clean.length > max ? `${clean.slice(0, max)}…` : clean;
}
